import { Turnstile, turnstileSiteKey } from '@/components/auth/Turnstile';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import PageLayout from '@/components/layout/PageLayout';
import { toast } from '@/hooks/use-toast';
import { SectionHeading } from '@/components/ui/section-heading';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    if (turnstileSiteKey && !captchaToken) return;
    setIsLoading(true);
    try {
      
      // PROD - Requires uncomment
      const baseUrl = 'https://www.omac-uk.co.uk';

      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        captchaToken,
        redirectTo: `${baseUrl}/auth/reset-password`
      });
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      // Always show success message for security (don't reveal if email exists)
      setEmailSent(true);
      toast({
        title: "Email Sent",
        description: "If an account exists with this email, you'll receive password reset instructions."
      });
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      // Still show success message for security (don't reveal if email exists)
      setEmailSent(true);
      toast({
        title: "Email Sent",
        description: "If an account exists with this email, you'll receive password reset instructions."
      });
    } finally {
      setCaptchaToken('');
      setCaptchaResetKey((key) => key + 1);
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="container max-w-md mx-auto py-16">
        <div className="space-y-6">
          <SectionHeading
            title="Forgot Password"
            gradient={true}
          />
          <p className="text-center text-gray-500">Enter your email to receive a password reset link</p>

          {emailSent ? (
            <div className="space-y-6">
              <Alert className="bg-green-50 border-green-100">
                <AlertDescription className="text-green-800">
                  If an account exists with this email, you'll receive password reset instructions shortly.
                  Please check your inbox and spam folder.
                </AlertDescription>
              </Alert>
              
              <div className="text-center">
                <Link to="/auth/login">
                  <Button variant="outline" className="mt-4">
                    Return to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="email@example.com" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Turnstile onToken={setCaptchaToken} resetKey={captchaResetKey} />

              <Button type="submit" className="w-full" disabled={isLoading || Boolean(turnstileSiteKey && !captchaToken)}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </Form>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Remember your password?{' '}
              <Link to="/auth/login" className="text-taekwondo-red hover:underline">
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
